import { c as _c } from "react/compiler-runtime";
import React, { useEffect, useMemo, useRef } from 'react';
import ConfigurableCmsValidity from '@spa/top-level-components/cms-page/metadata/configurable/configurable-cms-validity.component';
import getZenloopOrgId from '@spa/lib/configs/getZenloopOrgId';
import logger from '@spa/lib/logger';
type Zenloop = {
  recipient?: {
    properties?: Record<string, unknown>;
  };
};
type ZenloopSurveyRecipient = {
  properties?: Record<string, ReadonlyArray<string>>;
};
export interface SurveyProduct {
  /** Full breadcrumb trail. Sent as-is by the Classic embed. */
  readonly category?: string;
  /** Last breadcrumb entry only. Sent by the zenSurveys embed; must not leak into Classic. */
  readonly leafCategory?: string;
  readonly sku?: string;
  readonly name?: string;
  readonly url?: string;
}
declare global {
  var zl: unknown;
  var ZLoader: unknown;
  var Zenloop: Zenloop;
  var Zenloop_Survey_Recipient: ZenloopSurveyRecipient | undefined;
}
const ZENLOOP_LOADER_BASE_URL = 'https://website-overlay.zenloop.com/loader/zenloop.load.min.js';
const ZENLOOP_EMBEDDED_SURVEY_SCRIPT_URL = 'https://zenresponses.zenloop.com/assets/embeddedSurvey.js';
const ZENLOOP_EMBEDDED_SURVEY_SCRIPT_ID = 'zl-embedded-survey-loader';
const ZENLOOP_EMBEDDED_SURVEY_ELEMENT_NAME = 'zenloop-embedded-survey';
const ZENLOOP_ORG_ID_ENV_KEY = 'NEXT_PUBLIC_ZENLOOP_ORG_ID';

// A failed module fetch is cached in the document's module map, so re-injecting the same URL fails
// again without a network request. Each retry therefore uses a distinct URL (`?retry=<n>`); the first
// injection keeps the plain URL. Module-level so the count survives SPA navigation remounts.
let embeddedSurveyRetryCount = 0;
export interface SurveyComponentProps {
  readonly dateFrom: number | null;
  readonly dateTo: number | null;
  readonly editorMode: boolean;
  readonly product: SurveyProduct;
  readonly surveyId: string;
  readonly zenSurvey: boolean;
}
export default function SurveyComponent(t0) {
  const $ = _c(24);
  const {
    dateFrom,
    dateTo,
    editorMode,
    product,
    surveyId,
    zenSurvey
  } = t0;
  const zenContainerRef = useRef(null);
  let url;
  if ($[0] !== editorMode || $[1] !== surveyId) {
    url = new URL(ZENLOOP_LOADER_BASE_URL);
    url.searchParams.append("survey", surveyId);
    if (editorMode) {
      url.searchParams.append("isTest", "true");
    }
    $[0] = editorMode;
    $[1] = surveyId;
    $[2] = url;
  } else {
    url = $[2];
  }
  const surveyUrl = url;
  let t1;
  let t2;
  if ($[3] !== product || $[4] !== surveyId || $[5] !== surveyUrl || $[6] !== zenSurvey) {
    t1 = () => {
      let cleanup;
      if (!zenSurvey) {
        const script = document.createElement("script");
        script.src = surveyUrl.toString();
        script.id = "zl-website-overlay-loader";
        script.onerror = error => {
          logger.warn(`Failed to load Zenloop survey script with surveyId ${surveyId}: ${error}`);
        };
        script.async = true;
        const classicProperties = Object.fromEntries(Object.entries(product).filter(_temp));
        globalThis.Zenloop = globalThis.Zenloop || {};
        globalThis.Zenloop.recipient = {
          properties: {
            ...classicProperties
          }
        };
        document.body.appendChild(script);
        cleanup = () => {
          script.remove();
          globalThis.zl = undefined;
          globalThis.ZLoader = undefined;
          globalThis.Zenloop = {};
        };
      }
      return cleanup;
    };
    t2 = [surveyId, surveyUrl, product, zenSurvey];
    $[3] = product;
    $[4] = surveyId;
    $[5] = surveyUrl;
    $[6] = zenSurvey;
    $[7] = t1;
    $[8] = t2;
  } else {
    t1 = $[7];
    t2 = $[8];
  }
  useEffect(t1, t2);
  let t3;
  let t4;
  if ($[9] !== editorMode || $[10] !== product || $[11] !== surveyId || $[12] !== zenSurvey) {
    t3 = () => {
      let cleanup_0;
      const container = zenContainerRef.current;
      if (zenSurvey && !editorMode && container) {
        const orgId = getZenloopOrgId();
        if (orgId === null) {
          logger.warn(`Skipping the Zenloop zenSurveys embed: ${ZENLOOP_ORG_ID_ENV_KEY} is not configured`);
        } else {
          const properties = {};
          if (product.leafCategory) {
            properties.category = [product.leafCategory];
          }
          if (product.sku) {
            properties.sku = [product.sku];
          }
          if (product.name) {
            properties.name = [product.name];
          }
          if (product.url) {
            properties.url = [product.url];
          }
          const recipient = {
            properties
          };
          window.Zenloop_Survey_Recipient = recipient;
          if (!customElements.get(ZENLOOP_EMBEDDED_SURVEY_ELEMENT_NAME) && !document.getElementById(ZENLOOP_EMBEDDED_SURVEY_SCRIPT_ID)) {
            const script_0 = document.createElement("script");
            script_0.src = embeddedSurveyRetryCount === 0 ? ZENLOOP_EMBEDDED_SURVEY_SCRIPT_URL : `${ZENLOOP_EMBEDDED_SURVEY_SCRIPT_URL}?retry=${embeddedSurveyRetryCount}`;
            script_0.type = "module";
            script_0.id = ZENLOOP_EMBEDDED_SURVEY_SCRIPT_ID;
            script_0.onerror = () => {
              const currentSurveyId = document.querySelector(ZENLOOP_EMBEDDED_SURVEY_ELEMENT_NAME)?.getAttribute("surveyid");
              logger.warn(`Failed to load Zenloop zenSurveys script ${script_0.src} with surveyId ${currentSurveyId}`);
              script_0.remove();
              embeddedSurveyRetryCount = embeddedSurveyRetryCount + 1;
            };
            document.head.appendChild(script_0);
          }
          const element = document.createElement(ZENLOOP_EMBEDDED_SURVEY_ELEMENT_NAME);
          element.setAttribute("orgid", orgId);
          element.setAttribute("surveyid", surveyId);
          container.appendChild(element);
          cleanup_0 = () => {
            element.remove();
            if (window.Zenloop_Survey_Recipient === recipient) {
              window.Zenloop_Survey_Recipient = undefined;
            }
          };
        }
      }
      return cleanup_0;
    };
    t4 = [surveyId, product, zenSurvey, editorMode];
    $[9] = editorMode;
    $[10] = product;
    $[11] = surveyId;
    $[12] = zenSurvey;
    $[13] = t3;
    $[14] = t4;
  } else {
    t3 = $[13];
    t4 = $[14];
  }
  useEffect(t3, t4);
  let t5;
  if ($[15] !== dateFrom || $[16] !== dateTo || $[17] !== editorMode) {
    t5 = editorMode && <ConfigurableCmsValidity dateFrom={dateFrom} dateTo={dateTo} />;
    $[15] = dateFrom;
    $[16] = dateTo;
    $[17] = editorMode;
    $[18] = t5;
  } else {
    t5 = $[18];
  }
  let t6;
  if ($[19] !== zenSurvey) {
    t6 = zenSurvey ? <div ref={zenContainerRef} /> : <div id="zl-container" />;
    $[19] = zenSurvey;
    $[20] = t6;
  } else {
    t6 = $[20];
  }
  let t7;
  if ($[21] !== t5 || $[22] !== t6) {
    t7 = <>{t5}{t6}</>;
    $[21] = t5;
    $[22] = t6;
    $[23] = t7;
  } else {
    t7 = $[23];
  }
  return t7;
}
function _temp(t0) {
  const [key] = t0;
  return key !== "leafCategory";
}